import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { State } from "../../models/states.model";
import { statesService } from "../../service/state.service";
import PageHeader from "../PageHeader";

const initialState: State = {
  name: "",
  isActive: true,
  countryId: 0,
  stateCode: "",
  type: "",
};

const StatesComponent: React.FC = () => {
  const [states, setStates] = useState<State[]>([]);
  const [stateItem, setStateItem] = useState<State>(initialState);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedStateFilter, setSelectedStateFilter] = useState<State | null>(
    null
  );
  const [allStates, setAllStates] = useState<State[]>([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("statesTableFirst");
    return savedPage ? parseInt(savedPage) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRows = localStorage.getItem("statesTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { countryName, countryId } = location.state || {};
  const [error, setError] = useState("");

  const loadAllStates = async () => {
    try {
      const response = await statesService.getByCountryId(Number(countryId));
      setAllStates(response || []);
    } catch (err) {
      console.error("Error loading all states", err);
    }
  };

  useEffect(() => {
    if (!countryId) {
      navigate("/countries"); // fallback redirect
    } else {
      loadStates();
      loadAllStates(); // Load all states for the dropdown
    }
  }, [countryId]);

  const loadStates = async () => {
    setLoading(true);
    try {
      const response = await statesService.getByCountryId(Number(countryId));
      setStates(response || []);
    } catch (err) {
      console.error("Error loading states", err);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (stateItem.id) {
        await statesService.update(stateItem.id, stateItem);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "State updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        await statesService.create({ ...stateItem, countryId });
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "State created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      await loadStates();
      setShowForm(false);
      setStateItem(initialState);
    } catch {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Failed to save state.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
    setLoading(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setStateItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (rowData: State) => {
    if (!rowData.id) return;
    if (window.confirm(`Are you sure you want to delete "${rowData.name}"?`)) {
      await statesService.delete(rowData.id);
      await loadStates();
    }
  };

  const cityButtonTemplate = (rowData: State) => (
    <button
      className="btn btn-outline-info"
      onClick={() =>
        navigate("/cities", {
          state: {
            stateIdFromState: rowData.id,
            stateName: rowData.name,
            countryId,
            countryName,
          },
        })
      }
      style={{ padding: "0.35rem 0.75rem" }}
    >
      <Icon.ArrowRight />
    </button>
  );

  const actionBodyTemplate = (rowData: State) => (
    <div className="d-flex gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => {
          setStateItem(rowData);
          setShowForm(true);
        }}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      {/* Uncomment below to enable delete */}
      {/* <Button
                variant="link"
                className="p-0"
                onClick={() => handleDelete(rowData)}
                title="Delete"
                style={{ color: '#dc3545' }}
            >
                <Icon.Trash size={20} />
            </Button> */}
    </div>
  );

  // ✅ Filtered list based on globalFilter or selectedStateFilter
  const filteredStates = selectedStateFilter
    ? states.filter((s) => s.id === selectedStateFilter.id)
    : globalFilter
    ? states.filter((s: State) =>
        s.name.toLowerCase().includes(globalFilter.toLowerCase())
      )
    : states;

  return (
    <div className="content has-nobreadcrumbs">
      <PageHeader
        title={`States under country - ${countryName}`}
        showBackButton={false}
      />

      <div className="content-body">
        <div className="container mt-4">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <IconField iconPosition="left" style={{ width: "200px" }}>
              <InputIcon>
                <Icon.Search className="tx-12" />
              </InputIcon>
              <InputText
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  setSelectedStateFilter(null);
                  setCurrentPage(0);
                  localStorage.setItem("statesTableFirst", "0");
                }}
                placeholder="Search"
                style={{ height: "38px" }}
              />
            </IconField>
            <div className="d-flex align-items-center gap-3">
              <Dropdown
                value={selectedStateFilter}
                onChange={(e: { value: State | null }) => {
                  setSelectedStateFilter(e.value);
                  setCurrentPage(0);
                  setGlobalFilter("");
                }}
                options={allStates}
                optionLabel="name"
                placeholder="Select State"
                style={{ width: "200px", height: "38px" }}
                appendTo="self"
                showClear
                filter
                filterBy="name,stateCode"
              />
              <Button
                className="btn btn-primary"
                style={{ height: "38px", width: "200px" }}
                onClick={() => {
                  setStateItem({ ...initialState, countryId });
                  setShowForm(true);
                }}
              >
                <Icon.PlusCircle className="me-1" /> Create State
              </Button>
            </div>
          </div>

          <DataTable
            value={filteredStates}
            loading={loading}
            stripedRows
            paginator
            rows={rowsPerPage}
            first={currentPage}
            onPage={(e) => {
              setCurrentPage(e.first);
              setRowsPerPage(e.rows);
              localStorage.setItem("statesTableFirst", e.first.toString());
              localStorage.setItem("statesTableRows", e.rows.toString());
            }}
            rowsPerPageOptions={[5, 10, 20]}
            className="grid-table"
            emptyMessage="No states found"
          >
            <Column field="name" header="Name" sortable />
            <Column field="stateCode" header="State Code" sortable />
            <Column field="type" header="Type" sortable />
            <Column
              header="Cities"
              body={cityButtonTemplate}
              style={{ width: "100px", textAlign: "center" }}
            />
            <Column
              header="Actions"
              body={actionBodyTemplate}
              style={{ minWidth: "120px" }}
            />
          </DataTable>

          {showForm && (
            <div
              className="modal show d-block"
              tabIndex={-1}
              style={{ background: "rgba(0,0,0,0.3)" }}
            >
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {stateItem.id ? "Update State" : "Create State"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowForm(false);
                        setStateItem(initialState);
                      }}
                    ></button>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">State Name</label>
                        <input
                          className="form-control"
                          name="name"
                          value={stateItem.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">State Type</label>
                        <select
                          className="form-select"
                          name="type"
                          value={stateItem.type || ""}
                          onChange={handleChange}
                          required
                        >
                          <option value="">-- Select State Type --</option>
                          <option value="State">State</option>
                          <option value="Union Territory">
                            Union Territory
                          </option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">State Code</label>
                        <input
                          className="form-control"
                          name="stateCode"
                          value={stateItem.stateCode}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      {error && (
                        <div className="alert alert-danger">{error}</div>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading
                          ? stateItem.id
                            ? "Updating..."
                            : "Creating..."
                          : stateItem.id
                          ? "Update"
                          : "Create"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowForm(false);
                          setStateItem(initialState);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatesComponent;
